import { Paperclip, Loader2, MessageCircle, Table, FileText, X } from 'lucide-react';
import React from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

interface ChatFormProps {
  input: string;
  setInput: (value: string) => void;
  loading: boolean;
  onSend: (e: React.FormEvent) => void;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  image: File | null;
  setImage: (file: File | null) => void;
  allowAttachments?: boolean;
}

function ChatForm({
  input,
  setInput,
  loading,
  onSend,
  inputRef,
  image,
  setImage,
  allowAttachments = true,
}: ChatFormProps) {
  const fileInputId = React.useId();
  return (
    <form onSubmit={onSend} className="space-y-2">
      <div className="flex items-center gap-2 rounded-2xl border border-primary/50 bg-primary/10 backdrop-blur px-2 py-1.5 shadow-sm">
        {/* Attach */}
        {allowAttachments && (
          <>
            <input
              type="file"
              accept="image/*,.pdf,.txt,.md,.doc,.docx,.rtf,.xls,.xlsx,.csv,application/pdf,text/plain,text/markdown,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/rtf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              id={fileInputId}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setImage(file);
              }}
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => document.getElementById(fileInputId)?.click()}
              className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-muted/60 text-muted-foreground disabled:opacity-50"
              disabled={loading}
              aria-label="Allega file"
              title="Allega file"
            >
              <Paperclip className="w-4 h-4" />
            </button>
          </>
        )}

        {/* Textarea */}
        <div className="flex-1">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!loading && (input.trim() || image)) onSend(e);
              }
            }}
            rows={1}
            className="resize-none border-0 bg-transparent dark:bg-transparent shadow-none rounded-xl focus-visible:ring-0 focus-visible:border-0 h-11 min-h-0 leading-6 px-2"
            disabled={loading}
            placeholder="Scrivi un messaggio..."
          />
        </div>

        {/* Send */}
        <Button
          type="submit"
          disabled={loading || (!input.trim() && !image)}
          className="h-11 px-4 min-w-[96px] justify-center"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <MessageCircle className="w-4 h-4 mr-2" />
          )}
          Invia
        </Button>
      </div>

      {/* Image preview */}
      {allowAttachments && image && (
        <div className="flex items-center gap-2">
          {image.type.startsWith('image/') ? (
            <img
              src={URL.createObjectURL(image)}
              alt="preview"
              className="h-10 w-10 object-cover rounded border"
            />
          ) : (
            <div className="flex items-center gap-2 text-sm">
              {image.type.includes('sheet') || /\.(xls|xlsx)$/i.test(image.name) ? (
                <Table className="w-5 h-5 text-muted-foreground" />
              ) : (
                <FileText className="w-5 h-5 text-muted-foreground" />
              )}
              <span className="text-muted-foreground truncate max-w-[40ch]">{image.name}</span>
            </div>
          )}
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            onClick={() => setImage(null)}
          >
            <X className="w-4 h-4" /> Rimuovi allegato
          </button>
        </div>
      )}
    </form>
  );
}

export default ChatForm;
